import { Icons } from '@/src/modules/components/Icons';
import { Button } from '@/src/modules/components/Button';
import type { WizardData } from '@/src/types';

interface Props {
  data: WizardData;
  publishedUrl: string;
}

const OCCASION_LABELS: Record<string, string> = {
  WEDDING: 'Wedding',
  BIRTHDAY: 'Birthday',
  ANNIVERSARY: 'Anniversary',
  SEND_OFF: 'Send-Off',
  BABY_SHOWER: 'Baby Shower',
  ENGAGEMENT: 'Engagement',
  CUSTOM: 'Event',
};

function formatEventDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short', year: 'numeric', month: 'long', day: 'numeric',
  });
}

/**
 * Preview card shown on the publish success screen.
 *
 * Accepts WizardData (already in memory) and publishedUrl — no extra API call.
 * Share-focused; contrast with InvitationCard (dashboard list, ApiInvitation shape,
 * archive/restore/cancel actions).
 *
 * text-left is required: this card lives inside the overlay which sets text-center
 * on all descendants. We override it here so icon+text rows align correctly.
 */
export function InvitationPublishPreviewCard({ data, publishedUrl }: Props) {
  const cover = data.mediaUrls?.[0];
  const occasionLabel = OCCASION_LABELS[data.occasionType ?? ''] ?? 'Invitation';

  return (
    <div className="w-full max-w-sm mx-auto rounded-[2rem] border border-outline-variant/30 shadow-lg overflow-hidden bg-surface text-left">
      {/* Cover — image when available, styled gradient fallback otherwise */}
      <div className="h-44 relative overflow-hidden">
        {cover ? (
          <>
            <img
              src={cover}
              alt={data.eventName}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
            {/* Event name pinned to bottom of image */}
            <div className="absolute bottom-0 left-0 right-0 px-5 pb-4">
              <p className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-white/70 mb-1">
                {occasionLabel}
              </p>
              <h3 className="text-xl font-bold text-white leading-tight line-clamp-2">
                {data.eventName}
              </h3>
            </div>
          </>
        ) : (
          /* No image — render a branded gradient with event name */
          <div className="w-full h-full bg-gradient-to-br from-primary via-primary/80 to-brand-deep flex flex-col justify-end px-5 pb-4">
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-white/60 mb-1">
              {occasionLabel}
            </p>
            <h3 className="text-xl font-bold text-white leading-tight line-clamp-2">
              {data.eventName}
            </h3>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="px-5 py-4 flex flex-col gap-2">
        <div className="flex flex-col gap-1.5">
          {data.eventDate && (
            <div className="flex items-center gap-2 text-sm text-on-surface-variant font-medium">
              <Icons.Calendar size={14} className="text-primary flex-shrink-0" />
              <span>{formatEventDate(data.eventDate)}</span>
            </div>
          )}
          {data.eventVenue && (
            <div className="flex items-center gap-2 text-sm text-on-surface-variant font-medium">
              <Icons.MapPin size={14} className="text-primary flex-shrink-0" />
              <span className="line-clamp-1">{data.eventVenue}</span>
            </div>
          )}
          {data.hostName && (
            <div className="flex items-center gap-2 text-sm text-on-surface-variant font-medium">
              <Icons.Users size={14} className="text-primary flex-shrink-0" />
              <span>Hosted by {data.hostName}</span>
            </div>
          )}
        </div>

        {data.customMessage && (
          <p className="text-xs text-on-surface-variant italic line-clamp-2 border-t border-outline-variant/20 pt-2">
            &ldquo;{data.customMessage}&rdquo;
          </p>
        )}

        {/* View Invitation — Button with onClick so the <button> element
            handles the click directly. Wrapping <button> in <a> is invalid
            HTML and causes browsers to silently swallow the navigation. */}
        <Button
          className="w-full mt-1 flex items-center justify-center gap-2"
          onClick={() => window.open(publishedUrl, '_blank', 'noopener,noreferrer')}
        >
          <Icons.Eye size={15} />
          View Invitation
        </Button>
      </div>
    </div>
  );
}
